
import React, { useState, useEffect } from "react";
import {
    Box,
    Heading,
    Table,
    Tbody,
    Tr,
    Td,
    Flex,
    Text,
    Thead,
    Th,
    useToast,
    Spinner,
    Card,
    CardBody,
    CardHeader,
    Divider,
    Image,
    SimpleGrid,
    Tag,
    TagLabel,
    Badge
} from "@chakra-ui/react";
import {
    ChevronLeft,
    Package,
    DollarSign,
    ShoppingCart,
    Warehouse,
    Percent,
    Hash,
    Layers,
    Box as BoxIcon,
    FileCode
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { getInventoryDetailById } from "../../../redux/api/admin/InventoryApi";

function ProductDetailView() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [productData, setProductData] = useState(null);
    const toast = useToast();
    const id = location.state?.id;

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            getInventoryDetailById(id)
                .then((response) => {
                    console.log("getInventoryDetailById---->", response);
                    if (response.data.success && response.data.inventory && response.data.inventory.length > 0) {
                        console.log("Product Data:", response.data.inventory[0]);
                        const productData = response.data.inventory[0];
                        
                        // Parse itemImages if it's a string
                        if (typeof productData.itemImages === 'string') {
                            try {
                                productData.itemImages = JSON.parse(productData.itemImages);
                            } catch (e) {
                                console.error("Error parsing itemImages:", e);
                                productData.itemImages = [];
                            }
                        }
                        
                        setProductData(productData);
                    } else {
                        toast({
                            title: "No product data found",
                            status: "warning",
                            duration: 3000,
                            isClosable: true,
                        });
                    }
                })
                .catch((error) => {
                    console.log({ error });
                    toast({
                        title: "Error fetching Product details",
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [id, toast]);

    // Helper function to get status color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active': return 'green';
            case 'inactive': return 'gray';
            case 'discontinued': return 'red';
            case 'lowstock': return 'orange';
            default: return 'blue';
        }
    };

    // Calculate totals from warehouse details
    const calculateTotals = () => {
        if (!productData?.warehouseDetails) {
            return { totalStock: 0, totalValue: 0 };
        }

        return productData.warehouseDetails.reduce((acc, item) => {
            const stock = item.openingStock || 0;
            const valuePerUnit = item.openingStockValuePerUnit || 0;
            acc.totalStock += stock;
            acc.totalValue += stock * valuePerUnit;
            return acc;
        }, { totalStock: 0, totalValue: 0 });
    };

    const totals = calculateTotals();

    if (isLoading) {
        return (
            <Box className="h-[93vh] flex items-center justify-center">
                <div className="spinner-overlay">
          <Spinner size="xl" color="#011638" />
        </div>

            </Box>
        );
    }

    if (!productData) {
        return (
            <Box className="h-[93vh] flex items-center justify-center">
                <Text>No product data found</Text>
            </Box>
        );
    }

    return (
        <Box className="h-[93vh] flex flex-col mb-7">
            {/* Sticky Header */}
             <Box className="sticky top-0 z-10 bg-white shadow-md py-2 ps-2">
                  <div className="d-flex gap-2 justify-start items-center">
                    <ChevronLeft onClick={() => window.history.back()} className="hover:bg-gray-200 rounded-full "
                      style={{ cursor: 'pointer' }} />
                    <h4 className="mb-0">Mechanism Detail</h4>
                  </div>
                </Box>

            <Box className="flex-1 overflow-y-auto p-6">
                <Box flex="1" p={6} borderWidth="1px" borderRadius="lg" boxShadow="sm" bg="white" width={{ base: "100%" }}>
                    <Heading as="h3" size="md" mb={6} className="flex items-center gap-2">
                        <Package size={20} />
                        Item Information
                    </Heading>

                    <Table variant="simple" size="sm">
                        <Tbody>
                            <Tr>
                                <Td fontWeight="bold" width="40%">Item Name</Td>
                                <Td>{productData.itemName || "-"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Unit</Td>
                                <Td>{productData.unit || "-"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Category</Td>
                                <Td>{productData.categoryName || productData.category?.name || "-"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Sub Category</Td>
                                <Td>{productData.subCategory || productData.subCategoryName || productData.subCategory?.name || "-"}</Td>
                            </Tr>
                            <Tr>
                                <Td fontWeight="bold">Selling Price</Td>
                                <Td>
                                    <Text fontWeight="bold">{productData.sellingPrice ? `₹${parseFloat(productData.sellingPrice).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-"}</Text>
                                </Td>
                            </Tr>
                        </Tbody>
                    </Table>
                </Box>

                <Box mt={6} mb={20} p={4} borderWidth="1px" borderRadius="lg" boxShadow="sm" bg="white" w="100%">
                    <Heading as="h3" size="md" mb={4}>Description</Heading>
                    <Text whiteSpace="pre-wrap">{productData.description || productData.sellingDescription || "No description available"}</Text>
                </Box>
            </Box>
        </Box>
    );
}

export default ProductDetailView;

// import React, { useState, useEffect } from "react";
// import {
//     Box,
//     Heading,
//     Table,
//     Tbody,
//     Tr,
//     Td,
//     Flex,
//     Text,
//     Thead,
//     Th,
//     useToast,
//     Spinner,
//     Badge,
//     Image,
//     Stack,
//     Tag,
//     TagLabel,
//     SimpleGrid,
//     Divider,
//     Card,
//     CardBody,
//     CardHeader,
//     Grid,
//     GridItem
// } from "@chakra-ui/react";
// import {
//     ChevronLeft,
//     FileText,
//     Package,
//     Tag as TagIcon,
//     DollarSign,
//     ShoppingCart,
//     Warehouse,
//     Image as ImageIcon,
//     Layers,
//     Hash,
//     Box as BoxIcon,
//     Percent,
//     FileCode,
//     Scale,
//     Shield,
//     Zap,
//     Leaf,
//     Info
// } from "lucide-react";
// import { useLocation, useNavigate } from "react-router-dom";
// import { getInventoryDetailById } from "../../../redux/api/admin/InventoryApi";

// function ProductDetailView() {
//     const navigate = useNavigate();
//     const location = useLocation();
//     const [isLoading, setIsLoading] = useState(false);
//     const [productData, setProductData] = useState(null);
// console.log("productData@@@",productData)
//     const toast = useToast();
//     const productId = location.state?.id;
//     const isViewMode = location.state?.isViewMode;
//  const id = location.state?.id;

//       useEffect(() => {
//                 if (id) {
//                     setIsLoading(true);
//                     getInventoryDetailById(id)
//                         .then((response) => {
//                             console.log("getInventoryDetailById---->", response);
//                             if (response.data.success) {
//                                 console.log("Deal Data:", response.data.deals[0]);
//                                 const productData = response.data.deals[0];
//                                 setProductData(productData);
//                             }
//                         })
//                         .catch((error) => {
//                             console.log({ error });
//                             toast({
//                                 title: "Error fetching Product details",
//                                 status: "error",
//                                 duration: 3000,
//                                 isClosable: true,
//                             });
//                         })
//                         .finally(() => {
//                             setIsLoading(false);
//                         });
//                 }
//             }, [id, toast]);

//     // Mock data based on form structure
//     const mockProductData = {
//         id: "PROD-001",
//         itemName: "Premium Curtain Fabric",
//         sku: "FAB-PREMIUM-001",
//         type: "Fabric",
//         unit: "Meters",
//         categoryName: "Curtains",
//         subCategory: "Premium Range",
//         hscCode: "63031990",
//         availableStock: 150,
//         tax: "5", // 5%

//         // Media
//         itemImages: [
//             { id: 1, filePath: "https://via.placeholder.com/300x200/4A90E2/FFFFFF?text=Fabric+1", name: "fabric-blue.jpg" },
//             { id: 2, filePath: "https://via.placeholder.com/300x200/50C878/FFFFFF?text=Fabric+2", name: "fabric-green.jpg" },
//             { id: 3, filePath: "https://via.placeholder.com/300x200/F4A460/FFFFFF?text=Fabric+3", name: "fabric-beige.jpg" }
//         ],

//         // Specifications
//         fabric: "Polyester Blend",
//         gsm: "180",
//         uvBlackage: "95%",
//         opennessFactor: "3%",
//         greenGuard: "Yes",

//         // Selling Information
//         sellingPrice: 45.99,
//         sellingAccount: "Sales Revenue",
//         sellingDescription: "Premium quality curtain fabric with UV protection and green guard certification. Suitable for residential and commercial use.",

//         // Purchase Information
//         costPrice: 28.50,
//         purchaseAccount: "Inventory Purchase",
//         purchaseDescription: "Purchased from manufacturer with bulk discount. Imported from Italy.",
//         preferredVendor: "Italian Fabrics Ltd.",

//         // Warehouse Details
//         warehouseDetails: [
//             {
//                 warehouseName: "Main Warehouse",
//                 openingStock: 80,
//                 openingStockValuePerUnit: 28.50
//             },
//             {
//                 warehouseName: "North Branch",
//                 openingStock: 50,
//                 openingStockValuePerUnit: 28.50
//             },
//             {
//                 warehouseName: "South Branch",
//                 openingStock: 20,
//                 openingStockValuePerUnit: 28.50
//             }
//         ],

//         // Metadata
//         createdBy: "Admin User",
//         createdDate: "2024-01-15",
//         lastUpdated: "2024-03-20",
//         status: "Active"
//     };

//     // Fetch product details
//     useEffect(() => {
//         setIsLoading(true);

//         // Use mock data immediately
//         setTimeout(() => {
//             setProductData(mockProductData);
//             setIsLoading(false);

//             toast({
//                 title: "Product Data Loaded",
//                 status: "success",
//                 duration: 2000,
//                 isClosable: true,
//             });
//         }, 500);

//     }, []);

//     // Helper function to get status color
//     const getStatusColor = (status) => {
//         switch (status?.toLowerCase()) {
//             case 'active': return 'green';
//             case 'inactive': return 'gray';
//             case 'discontinued': return 'red';
//             case 'lowstock': return 'orange';
//             default: return 'blue';
//         }
//     };

//     // Calculate totals from warehouse details
//     const calculateTotals = () => {
//         if (!productData?.warehouseDetails) {
//             return { totalStock: 0, totalValue: 0 };
//         }

//         return productData.warehouseDetails.reduce((acc, item) => {
//             acc.totalStock += item.openingStock || 0;
//             acc.totalValue += (item.openingStock || 0) * (item.openingStockValuePerUnit || 0);
//             return acc;
//         }, { totalStock: 0, totalValue: 0 });
//     };

//     const totals = calculateTotals();

//     if (isLoading) {
//         return (
//             <Box className="h-[93vh] flex items-center justify-center">
//                 <Spinner size="xl" />
//                 <Text ml={4}>Loading Product Data...</Text>
//             </Box>
//         );
//     }

//     if (!productData) {
//         return (
//             <Box className="h-[93vh] flex items-center justify-center">
//                 <Text>No product data found</Text>
//             </Box>
//         );
//     }

//     return (
//         <Box className="h-[93vh] flex flex-col mb-7">
//             {/* Sticky Header */}
//             <Box className="d-flex align-items start justify-content-between sticky top-0 z-10 bg-white shadow-md ps-2 py-2">
//                 <span className='d-flex gap-2 justify-start items-center'>
//                     <ChevronLeft
//                         onClick={() => navigate(-1)}
//                         className="hover:bg-gray-200 rounded-full cursor-pointer"
//                         size={24}
//                     />
//                     <h4 className='mb-0'>
//                         Product Detail  
//                     </h4>
//                 </span>
             
//             </Box>

//             <Box className="flex-1 overflow-y-auto p-6">
//                 {/* Top Section - 2 Columns */}
//                 <Flex gap={6} direction={{ base: "column", md: "row" }} mt={4} flexWrap="wrap">
//                     {/* Left Column - Basic Information */}
//                     <Box
//                         flex="1"
//                         p={6}
//                         borderWidth="1px"
//                         borderRadius="lg"
//                         boxShadow="sm"
//                         bg="white"
//                         width={{ base: "100%", md: "50%" }}
//                     >
//                         <Heading as="h3" size="md" mb={6} className="flex items-center gap-2">
//                             <Package size={20} />
//                             Item  Information
//                         </Heading>

//                         <Table variant="simple" size="sm">
//                             <Tbody>
//                                 <Tr>
//                                     <Td fontWeight="bold" width="40%" className="align-top">Item Name</Td>
//                                     <Td>{productData.itemName || "-"}</Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">SKU</Td>
//                                     <Td>
//                                         <Flex align="center" gap={2}>
//                                             {/* <Hash size={16} /> */}
//                                             <Text>{productData.sku || "-"}</Text>
//                                         </Flex>
//                                     </Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">Type</Td>
//                                     <Td>{productData.type || "-"}</Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">Unit</Td>
//                                     <Td>{productData.unit || "-"}</Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">Category</Td>
//                                     <Td>{productData.categoryName || "-"}</Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">Sub Category</Td>
//                                     <Td>{productData.subCategory || "-"}</Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">HSC Code</Td>
//                                     <Td>
//                                         <Flex align="center" gap={2}>
//                                             {/* <FileCode size={16} /> */}
//                                             <Text>{productData.hscCode || "-"}</Text>
//                                         </Flex>
//                                     </Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">Availble Stock</Td>
//                                     <Td>
//                                         <Flex align="center" gap={2}>
//                                             {/* <FileCode size={16} /> */}
//                                             <Text>{productData.hscCode || "-"}</Text>
//                                         </Flex>
//                                     </Td>
//                                 </Tr>
//                                 <Tr>
//                                     <Td fontWeight="bold">Tax Rate</Td>
//                                     <Td>
//                                         <Flex align="center" gap={2}>
//                                             {/* <Percent size={16} /> */}
//                                             <Text>{productData.tax ? `${productData.tax}` : "-"}</Text>
//                                         </Flex>
//                                     </Td>
//                                 </Tr>
//                             </Tbody>
//                         </Table>

//                         <Divider my={4} />

//                     </Box>

//                 </Flex>

//                 {/* Middle Section - 2 Columns */}
//                 <Flex gap={6} mb={6} mt={6} direction={{ base: "column", md: "row" }} flexWrap="wrap">
//                     {/* Sales Information */}
//                     <Card flex="1" width={{ base: "100%", md: "50%" }}>
//                         <CardHeader>
//                             <Heading as="h3" size="md" className="flex items-center gap-2">
//                                 <DollarSign size={20} />
//                                 Sales Information
//                             </Heading>
//                         </CardHeader>
//                         <CardBody>
//                             <Table variant="simple" size="sm">
//                                 <Tbody>
//                                     <Tr>
//                                         <Td fontWeight="bold" width="40%">Selling Price</Td>
//                                         <Td>
//                                             <Flex align="center" gap={2}>
//                                                 {/* <DollarSign size={16} /> */}
//                                                 <Text fontSize="lg" fontWeight="bold" color="">
//                                                     ${productData.sellingPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
//                                                 </Text>
//                                             </Flex>
//                                         </Td>
//                                     </Tr>
//                                     <Tr>
//                                         <Td fontWeight="bold">Selling Account</Td>
//                                         <Td>
//                                             <Text fontWeight="medium">{productData.sellingAccount || "-"}</Text>
//                                         </Td>
//                                     </Tr>
//                                     <Tr>
//                                         <Td fontWeight="bold" className="align-top">Selling Description</Td>
//                                         <Td>
                                         
                                    
//                                                 <Text fontSize="sm">
//                                                     {productData.sellingDescription || "No description available"}
//                                                 </Text>
                                            
//                                         </Td>
//                                     </Tr>
//                                 </Tbody>
//                             </Table>
//                         </CardBody>
//                     </Card>

//                     <Card flex="1" width={{ base: "100%", md: "50%" }}>
//                         <CardHeader>
//                             <Heading as="h3" size="md" className="flex items-center gap-2">
//                                 <ShoppingCart size={20} />
//                                 Purchase Information
//                             </Heading>
//                         </CardHeader>
//                         <CardBody>
//                             <Table variant="simple" size="sm">
//                                 <Tbody>
//                                     <Tr>
//                                         <Td fontWeight="bold" width="40%">Cost Price</Td>
//                                         <Td>
//                                             <Flex align="center" gap={2}>
//                                                 {/* <DollarSign size={16} /> */}
//                                                 <Text fontSize="lg" fontWeight="bold" color="">
//                                                     ${productData.costPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
//                                                 </Text>
//                                             </Flex>
//                                         </Td>
//                                     </Tr>

//                                     <Tr>
//                                         <Td fontWeight="bold">Purchase Account</Td>
//                                         <Td>
//                                             <Text fontWeight="medium">{productData.purchaseAccount || "-"}</Text>
//                                         </Td>
//                                     </Tr>

//                                     {/* {productData.preferredVendor && (
//                                         <Tr>
//                                             <Td fontWeight="bold">Preferred Vendor</Td>
//                                             <Td>
//                                                 <Tag size="md" borderRadius="full" px={3}>
//                                                     <TagLabel>{productData.preferredVendor}</TagLabel>
//                                                 </Tag>
//                                             </Td>
//                                         </Tr>
//                                     )} */}

//                                     <Tr>
//                                         <Td fontWeight="bold" className="align-top">Purchase Description</Td>
//                                         <Td>
                                        
//                                                 <Text fontSize="sm">
//                                                     {productData.purchaseDescription || "No description available"}
//                                                 </Text>
                                            
//                                         </Td>
//                                     </Tr>
//                                 </Tbody>
//                             </Table>

//                             {/* Margin Calculation */}
                            
//                         </CardBody>
//                     </Card>
//                 </Flex>

//                 {/* Warehouse Details Section */}
//                 <Card mb={6}>
//                     <CardHeader>
//                         <Flex justify="space-between" align="center">
//                             <Heading as="h3" size="md" className="flex items-center gap-2">
//                                 <Warehouse size={20} />
//                                 Warehouse Details
//                             </Heading>

//                         </Flex>
//                     </CardHeader>
//                     <CardBody>
//                         <Box overflowX="auto">
//                             <Table variant="simple" size="md" minWidth="800px">
//                                 <Thead bg="gray.100">
//                                     <Tr>
//                                         <Th width="10%">S.No.</Th>
//                                         <Th width="30%">Warehouse Name</Th>
//                                         <Th width="20%" textAlign="right">Opening Stock</Th>
//                                         <Th width="20%" textAlign="right">Value Per Unit</Th>
//                                         <Th width="20%" textAlign="right">Total Value</Th>
//                                     </Tr>
//                                 </Thead>
//                                 <Tbody>
//                                     {productData.warehouseDetails && productData.warehouseDetails.length > 0 ? (
//                                         productData.warehouseDetails.map((warehouse, index) => {
//                                             const totalValue = (warehouse.openingStock || 0) * (warehouse.openingStockValuePerUnit || 0);
//                                             return (
//                                                 <Tr key={index} _hover={{ bg: "gray.50" }}>
//                                                     <Td fontWeight="bold">{index + 1}</Td>
//                                                     <Td>
//                                                         <Flex align="center" gap={2}>
                                                        
//                                                             <Text fontWeight="medium">{warehouse.warehouseName}</Text>
//                                                         </Flex>
//                                                     </Td>
//                                                     <Td textAlign="right">
//                                                         <Text>
//                                                             {warehouse.openingStock?.toLocaleString() || "0"} {productData.unit}
//                                                         </Text>
//                                                     </Td>
//                                                     <Td textAlign="right">
//                                                         <Text>
//                                                             ${warehouse.openingStockValuePerUnit?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || "0.00"}
//                                                         </Text>
//                                                     </Td>
//                                                     <Td textAlign="right">
//                                                         <Text fontWeight="" color="">
//                                                             ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
//                                                         </Text>
//                                                     </Td>
//                                                 </Tr>
//                                             );
//                                         })
//                                     ) : (
//                                         <Tr>
//                                             <Td colSpan="5" textAlign="center" py={6}>
//                                                 <Flex direction="column" align="center" gap={2}>
//                                                     <Warehouse size={40} color="gray.400" />
//                                                     <Text color="gray.500">No warehouse data available</Text>
//                                                 </Flex>
//                                             </Td>
//                                         </Tr>
//                                     )}

//                                 </Tbody>
//                             </Table>
//                         </Box>

//                     </CardBody>
//                 </Card>


//             </Box>
//         </Box>
//     );
// }

// export default ProductDetailView;